import apiClient from '../client';
import { InvoiceDto, InvoiceGenerationDto, InvoiceUpdateRequestDto } from 'interfaces/orders/invoice';
import { OrderBranchNoCompanyError, OrderStatusNotEligibleError } from './invoiceErrors';
import { invoicingRequest } from '../invoicingErrors';

export { OrderStatusNotEligibleError, OrderBranchNoCompanyError } from './invoiceErrors';

const withKnownInvoiceErrors = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (error: any) {
    if (error?.code === 'ORDER_STATUS_NOT_ELIGIBLE') {
      throw new OrderStatusNotEligibleError();
    }
    if (error?.code === 'ORDER_BRANCH_NO_COMPANY') {
      throw new OrderBranchNoCompanyError();
    }
    throw error;
  }
};

interface InvoiceService {
  list(orderId: string): Promise<InvoiceDto[]>;
  generate(orderId: string): Promise<InvoiceDto>;
  generation(orderId: string): Promise<InvoiceGenerationDto>;
  update(orderId: string, invoiceId: string, data: InvoiceUpdateRequestDto): Promise<InvoiceDto>;
  get(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  issue(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  submit(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  cancel(orderId: string, invoiceId: string): Promise<InvoiceDto>;
}

const endpoints = {
  list: (orderId: string) => `/orders/${orderId}/invoices`,
  generate: (orderId: string) => `/orders/${orderId}/invoices`,
  generation: (orderId: string) => `/orders/${orderId}/invoices/generation`,
  get: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}`,
  issue: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/issue`,
  submit: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/submit`,
  cancel: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/cancel`,
};

/**
 * Every rejection surfaces as an `InvoicingApiError` (code + details), except the two order-level
 * codes that have their own error classes.
 */
export const invoicesService: InvoiceService = {
  list: orderId => invoicingRequest(() => apiClient.get(endpoints.list(orderId))),
  generate: orderId => withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.post(endpoints.generate(orderId)))),
  update: (orderId, invoiceId, data) =>
    withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.patch(endpoints.get(orderId, invoiceId), data))),
  get: (orderId, invoiceId) => invoicingRequest(() => apiClient.get(endpoints.get(orderId, invoiceId))),
  generation: orderId => invoicingRequest(() => apiClient.get(endpoints.generation(orderId))),
  issue: (orderId, invoiceId) => withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.post(endpoints.issue(orderId, invoiceId)))),
  submit: (orderId, invoiceId) =>
    withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.post(endpoints.submit(orderId, invoiceId)))),
  cancel: (orderId, invoiceId) =>
    withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.post(endpoints.cancel(orderId, invoiceId)))),
};
